import React from 'react';
import PropTypes from 'prop-types';

import {Box, IconButton, Stack, TextField, Typography} from 'views/components';
import {ArrowCircleRight as ArrowCircleRightIcon} from 'views/icons';
import {translate, Translate} from 'views/module';

import * as chrono from 'chrono-node';
import moment from 'moment';

const CustomDatePane = (props) => {
  const {
    dueDate,
    handleSelectReminderDate,
  } = props;

  const [customDateText, setCustomDateText] = React.useState('');

  const rootRef = React.useRef(null);

  React.useEffect(() => {
    rootRef.current?.focus({preventScroll: true});
  }, [rootRef]);

  const parseReminderDateAndTimeFromText = (text) => {
    if (!text.trim()) {
      return null;
    }

    const referenceDate = moment().startOf('day').toDate();
    const parsedResult = chrono.parse(text, referenceDate, {forwardDate: true})[0];

    if (!parsedResult) {
      return null;
    }

    const parsedDate = moment(parsedResult.start.date());
    const hasExplicitTime = parsedResult.start.isCertain('hour') || parsedResult.start.isCertain('minute');

    if (!parsedDate.isValid()) {
      return null;
    }

    if (!hasExplicitTime) {
      return null;
    }

    const resolvedDate = parsedDate.second(0).millisecond(0);

    return resolvedDate.utc().toISOString();
  };

  const feedback = React.useMemo(() => {
    const parsedReminderDateAndTime = parseReminderDateAndTimeFromText(customDateText);

    if (!parsedReminderDateAndTime) {
      return {
        state: null,
        message: null,
        color: null,
      };
    }

    const latestUserSpecifiedMoment = moment.utc(dueDate).clone().subtract(1, 'hour');
    if (moment(parsedReminderDateAndTime).utc().isAfter(latestUserSpecifiedMoment)) {
      return {
        state: 'DATE_TOO_LATE',
        message: translate('tasks.Common.ReminderPicker.Custom.DateTooLate'),
        color: 'error.main',
      };
    }

    const localValue = moment(parsedReminderDateAndTime).local();

    return {
      state: 'VALID_DATE',
      message: localValue.isSame(moment(), 'day') ?
        translate('tasks.Common.ReminderPicker.TodayWithTime', {
          time: localValue.format('h:mmA'),
        }) :
        localValue.format('ddd MMM D h:mmA'),
      color: 'info.main',
    };
  }, [customDateText, dueDate]);

  const onSubmit = React.useCallback(() => {
    if (feedback.state !== 'VALID_DATE') {
      return;
    }

    handleSelectReminderDate(parseReminderDateAndTimeFromText(customDateText));
  }, [customDateText, feedback, handleSelectReminderDate]);


  return (
    <Stack
      ref={rootRef}
      spacing={1.5}
      tabIndex={0}
    >
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{
          border: '1px solid #D5D1CB',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          px: 1,
        }}
      >
        <TextField
          fullWidth
          variant='outlined'
          value={customDateText}
          onChange={(newValue) => setCustomDateText(newValue)}
          placeholder={translate('tasks.Common.ReminderPicker.Custom.Placeholder')}
          sx={{
            '& .MuiOutlinedInput-root': {
              p: 0,
              borderRadius: 0,
              backgroundColor: 'transparent',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
            '& .MuiOutlinedInput-input': {
              py: 1.25,
              px: 0,
            },
          }}
        />
        <IconButton
          onClick={onSubmit}
          disabled={feedback.state !== 'VALID_DATE'}
          sx={{
            color: 'primary.dark',
            p: 0.25,
            height: '24px',
            width: '24px',
          }}
        >
          <ArrowCircleRightIcon />
        </IconButton>
      </Stack>
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: 290,
          pr: 0.5,
        }}
      >
        <Stack spacing={0.25}>
          {
            feedback.state &&
            <Typography sx={{
              fontWeight: 500,
              color: feedback.color,
              fontSize: '14px',
            }}
            >
              {feedback.message}
            </Typography>
          }
          <Typography sx={{
            fontWeight: 400,
            color: '#696969',
            fontSize: '14px',
          }}
          >
            <Translate expression='tasks.Common.ReminderPicker.Custom.Description' />
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

CustomDatePane.propTypes = {
  dueDate: PropTypes.string.isRequired,
  handleSelectReminderDate: PropTypes.func.isRequired,
};

export default React.memo(CustomDatePane);
